import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getUpcomingEvents, getOverdueSummary } from '../lib/api';
import { 
    LayoutDashboard, 
    Users, 
    FolderKanban, 
    Calendar, 
    FileText, 
    BarChart3, 
    Settings, 
    LogOut,
    Menu,
    X,
    Bell,
    ChevronDown,
    UserCircle,
    Gift,
    HardHat,
    Sun,
    Moon,
    AlertTriangle,
    Scale,
    Clock,
    Building2,
    Upload,
    Trash2
} from 'lucide-react';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b32b0f5b-4b52-4c01-8ed0-3269ece4932a/artifacts/gm1lpq5e_Capture%20d%E2%80%99e%CC%81cran%202026-04-08%20a%CC%80%2016.29.35.png";

const navItems = [
    { path: '/', label: 'Tableau de bord', icon: LayoutDashboard },
    { path: '/prospects', label: 'Prospects / Clients', icon: Users },
    { path: '/syndics', label: 'Copropriétés', icon: Building2 },
    { path: '/projets', label: 'Projets', icon: HardHat },
    { path: '/en-retard', label: 'En retard', icon: AlertTriangle, badgeKey: 'overdueTotal' },
    { path: '/litiges', label: 'Litiges', icon: Scale },
    { path: '/agenda', label: 'Agenda', icon: Calendar },
    { path: '/parrainages', label: 'Parrainages', icon: Gift },
    { path: '/reporting', label: 'Reporting', icon: BarChart3 },
    { path: '/import', label: 'Import Excel', icon: Upload, roles: ['admin'] },
    { path: '/corbeille', label: 'Corbeille', icon: Trash2, roles: ['admin'] },
    { path: '/parametres', label: 'Paramètres', icon: Settings, roles: ['admin', 'manager'] },
];

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [upcoming, setUpcoming] = useState({ rdv_sans_devis_72h: [], rdvs: [], relances: [] });
    const [overdueSummary, setOverdueSummary] = useState({ total: 0, rdv_sans_devis: 0, devis_sans_retour: 0, relances_en_retard: 0, chantiers_a_receptionner: 0, clients_silencieux: 0 });
    const [seenIds, setSeenIds] = useState(() => {
        try {
            const raw = localStorage.getItem('seen_notif_ids');
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [popoverOpen, setPopoverOpen] = useState(false);

    useEffect(() => {
        const fetchNotif = async () => {
            try {
                const res = await getUpcomingEvents();
                setUpcoming(res.data || {});
            } catch {}
            try {
                const res2 = await getOverdueSummary();
                setOverdueSummary(res2.data || {});
            } catch {}
        };
        if (user) {
            fetchNotif();
            const interval = setInterval(fetchNotif, 60000);
            return () => clearInterval(interval);
        }
    }, [user]);

    // Collect all notification IDs currently visible
    const allNotifIds = [
        ...(upcoming.rdv_sans_devis_72h || []).map(r => `72h:${r.id}`),
        ...(upcoming.rdvs || []).map(r => `rdv:${r.id}`),
        ...(upcoming.relances || []).map(r => `rel:${r.id}`)
    ];
    const newCount = allNotifIds.filter(id => !seenIds.includes(id)).length;
    const totalNotifs = allNotifIds.length;

    const markAllSeen = () => {
        const updated = Array.from(new Set([...seenIds, ...allNotifIds]));
        setSeenIds(updated);
        try {
            localStorage.setItem('seen_notif_ids', JSON.stringify(updated));
        } catch {}
    };

    // When user opens the popover, flag these as seen
    useEffect(() => {
        if (popoverOpen && allNotifIds.length > 0) {
            // Delay a bit so the "NEW" badges are visible for a moment
            const timer = setTimeout(markAllSeen, 1500);
            return () => clearTimeout(timer);
        }
    }, [popoverOpen, allNotifIds.length]);

    const isNew = (id) => !seenIds.includes(id);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const filteredNavItems = navItems.filter(item => 
        !item.roles || item.roles.includes(user?.role)
    );

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="main-layout">
            {/* Mobile Header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E4E4E7] z-50 flex items-center justify-between px-4">
                <button 
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-2 hover:bg-[#F4F4F5] rounded-sm"
                    data-testid="mobile-menu-toggle"
                >
                    {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
                <img src={LOGO_URL} alt="UNeedServices" className="h-8" />
                <div className="w-10" />
            </div>

            {/* Sidebar */}
            <aside className={`fixed top-0 left-0 h-full w-64 bg-[#0A0A0A] text-white z-40 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="sidebar">
                <div className="h-16 flex items-center justify-center border-b border-white/10">
                    <img src={LOGO_URL} alt="UNeedServices" className="h-10" />
                </div>
                
                <nav className="p-4 space-y-1">
                    {filteredNavItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        const badgeCount = item.badgeKey === 'overdueTotal' ? overdueSummary.total : 0;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setSidebarOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200 ${
                                    isActive 
                                        ? 'bg-[#E85A24] text-white' 
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                                }`}
                                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                            >
                                <Icon className="w-5 h-5" />
                                <span className="font-medium flex-1">{item.label}</span>
                                {badgeCount > 0 && (
                                    <span
                                        className="min-w-[20px] h-5 px-1.5 bg-[#DC2626] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                                        data-testid={`nav-badge-${item.path.replace('/', '')}`}
                                    >
                                        {badgeCount > 99 ? '99+' : badgeCount}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
                    <div className="flex items-center gap-3 px-4 py-2">
                        <div className="w-10 h-10 rounded-full bg-[#E85A24] flex items-center justify-center text-white font-bold">
                            {user?.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user?.name}</p>
                            <p className="text-xs text-white/50 capitalize">{user?.role}</p>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <div className="lg:ml-64">
                {/* Top Bar */}
                <header className="h-16 bg-white border-b border-[#E4E4E7] flex items-center justify-end px-3 sm:px-6 lg:sticky top-0 z-30 mt-16 lg:mt-0">
                    <div className="flex items-center gap-2">
                        {/* Theme toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            data-testid="theme-toggle-btn"
                            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
                        >
                            {theme === 'dark' ? (
                                <Sun className="w-5 h-5 text-[#F59E0B]" />
                            ) : (
                                <Moon className="w-5 h-5 text-[#52525B]" />
                            )}
                        </Button>

                        {/* Notifications */}
                        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                            <PopoverTrigger asChild>
                                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                                    <Bell className="w-5 h-5 text-[#52525B]" />
                                    {newCount > 0 && (
                                        <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#E85A24] rounded-full text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-new-count">
                                            {newCount > 9 ? '9+' : newCount}
                                        </span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-96 p-0 bg-white" data-testid="notifications-panel">
                                <div className="p-4 border-b border-[#E4E4E7] flex items-center justify-between">
                                    <div>
                                        <h3 className="font-bold text-[#0A0A0A]">Notifications</h3>
                                        <p className="text-xs text-[#52525B]">
                                            {totalNotifs} total · {newCount > 0 ? <span className="text-[#E85A24] font-semibold">{newCount} nouvelle(s)</span> : 'Toutes vues'}
                                        </p>
                                    </div>
                                    {newCount > 0 && (
                                        <button
                                            onClick={markAllSeen}
                                            className="text-xs text-[#E85A24] hover:underline"
                                            data-testid="mark-all-seen-btn"
                                        >
                                            Tout marquer lu
                                        </button>
                                    )}
                                </div>
                                <div className="max-h-[400px] overflow-y-auto">
                                    {totalNotifs === 0 && overdueSummary.total === 0 && (
                                        <p className="p-6 text-sm text-[#52525B] text-center" data-testid="no-notifications">Aucune alerte pour le moment</p>
                                    )}
                                    {overdueSummary.total > 0 && (
                                        <button
                                            onClick={() => { setPopoverOpen(false); navigate('/en-retard'); }}
                                            className="w-full p-3 border-b border-[#E4E4E7] hover:bg-red-50 transition-colors text-left flex items-center justify-between gap-3"
                                            data-testid="notif-overdue-shortcut"
                                        >
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle className="w-5 h-5 text-[#DC2626]" />
                                                <div>
                                                    <p className="text-sm font-bold text-[#DC2626]">{overdueSummary.total} items en retard</p>
                                                    <p className="text-xs text-[#52525B]">
                                                        RDV: {overdueSummary.rdv_sans_devis} · Devis: {overdueSummary.devis_sans_retour} · Relances: {overdueSummary.relances_en_retard} · Chantiers: {overdueSummary.chantiers_a_receptionner} · Silencieux: {overdueSummary.clients_silencieux}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className="text-xs font-bold text-[#DC2626]">Voir →</span>
                                        </button>
                                    )}
                                    {upcoming.rdv_sans_devis_72h?.length > 0 && (
                                        <div className="p-3 border-b border-[#E4E4E7]">
                                            <p className="text-xs font-bold uppercase text-red-600 flex items-center gap-1 mb-2">
                                                <AlertTriangle className="w-3 h-3" /> RDV sans devis &gt; 72h
                                            </p>
                                            {upcoming.rdv_sans_devis_72h.slice(0, 5).map(r => (
                                                <button
                                                    key={r.id}
                                                    onClick={() => navigate(`/dossiers/${r.id}`)}
                                                    className="w-full text-left px-2 py-2 text-sm hover:bg-[#FAFAFA] rounded-sm block"
                                                    data-testid={`notif-72h-${r.id}`}
                                                >
                                                    <span className="flex items-center gap-2">
                                                        <span className="font-medium flex-1 truncate">{r.prospect_nom || r.titre}</span>
                                                        {isNew(`72h:${r.id}`) && (
                                                            <span className="bg-[#E85A24] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full" data-testid={`notif-new-72h-${r.id}`}>NEW</span>
                                                        )}
                                                    </span>
                                                    <span className="block text-xs text-[#52525B]">
                                                        RDV du {r.date_rdv ? new Date(r.date_rdv).toLocaleDateString('fr-FR') : '-'}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {upcoming.rdvs?.length > 0 && (
                                        <div className="p-3 border-b border-[#E4E4E7]">
                                            <p className="text-xs font-bold uppercase text-[#E85A24] flex items-center gap-1 mb-2">
                                                <Clock className="w-3 h-3" /> RDV à venir
                                            </p>
                                            {upcoming.rdvs.slice(0, 5).map(r => (
                                                <button
                                                    key={r.id}
                                                    onClick={() => navigate(`/dossiers/${r.id}`)}
                                                    className="w-full text-left px-2 py-2 text-sm hover:bg-[#FAFAFA] rounded-sm block"
                                                >
                                                    <span className="flex items-center gap-2">
                                                        <span className="font-medium flex-1 truncate">{r.prospect_nom || r.titre}</span>
                                                        {isNew(`rdv:${r.id}`) && (
                                                            <span className="bg-[#E85A24] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">NEW</span>
                                                        )}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {upcoming.relances?.length > 0 && (
                                        <div className="p-3">
                                            <p className="text-xs font-bold uppercase text-[#D97706] flex items-center gap-1 mb-2">
                                                <Clock className="w-3 h-3" /> Relances
                                            </p>
                                            {upcoming.relances.slice(0, 5).map(r => (
                                                <button
                                                    key={r.id}
                                                    onClick={() => r.dossier_id && navigate(`/dossiers/${r.dossier_id}`)}
                                                    className="w-full text-left px-2 py-2 text-sm hover:bg-[#FAFAFA] rounded-sm block"
                                                >
                                                    <span className="flex items-center gap-2">
                                                        <span className="font-medium flex-1 truncate">{r.titre || r.type}</span>
                                                        {isNew(`rel:${r.id}`) && (
                                                            <span className="bg-[#E85A24] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">NEW</span>
                                                        )}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                        
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                                    <UserCircle className="w-5 h-5 text-[#52525B]" />
                                    <span className="hidden md:inline text-sm font-medium">{user?.name}</span>
                                    <ChevronDown className="w-4 h-4 text-[#52525B]" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                {(user?.role === 'admin' || user?.role === 'manager') && (
                                    <DropdownMenuItem onClick={() => navigate('/parametres')} data-testid="menu-settings">
                                        <Settings className="w-4 h-4 mr-2" />
                                        Paramètres
                                    </DropdownMenuItem>
                                )}
                                {(user?.role === 'admin' || user?.role === 'manager') && <DropdownMenuSeparator />}
                                <DropdownMenuItem onClick={handleLogout} className="text-[#DC2626]" data-testid="menu-logout">
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Déconnexion
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Page Content */}
                <main className="p-3 sm:p-4 lg:p-6">
                    {children}
                </main>
            </div>

            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}
        </div>
    );
};
